import ApiHosts from "@/libs/ApiHosts";
import ApiResponseCache from "@/libs/ApiResponseCache";

/**
 * 신규 가입 웰컴 쿠폰의 **공개 오퍼** — "지금 가입하면 무엇을 받는가".
 *
 * mgmt `GET /api/v1/coupons/welcome-offer` 의 응답 모양이다. 필드가 셋뿐인 것이 설계다:
 * 종전에는 프론트가 할인율·유효기간·**발급 마감 시각**을 상수로 미러하고 있었는데
 * (~~`src/constants/welcome-coupon.ts`~~, 2026-09-10 제거) 정본(mgmt 캠페인 정의)과 갈리는 순간
 * ①약속만 하고 안 주거나 ②줄 수 있는데 안 권하는 상태가 됐다. 2026-08-14 마감 직후 실제로
 * ①이 발생했고, 캠페인마다 프론트 배포가 필요했다.
 */
export type WelcomeCouponOffer = {
  /**
   * 지금 신규 가입으로 쿠폰이 **실제 발급되는가**. `false` 면 나머지 필드는 의미가 없다.
   *
   * 킬 스위치(`welcomeCouponEnabled`)·캠페인 활성·발급 창·할인율 범위·즉시만료를 **서버가
   * 전부 흡수한 결과**다. 그래서 프론트는 발급 마감 시각을 알 필요가 없고, 응답에도 담기지
   * 않는다 — 담기면 그걸로 자체 판정하는 미러가 다시 생긴다.
   */
  available: boolean;
  /** 할인율(정수 퍼센트). 고지 문구에 그대로 쓰인다. */
  percent: number;
  /**
   * 지금 발급받으면 몇 시간 쓸 수 있는가. 서버가 만료 모드를 흡수한 값이라 프론트는
   * 상대/절대 만료를 구분하지 않는다. 캠페인 유효기간이 48·72·168시간으로 섞여 있어
   * 시 단위이며, "N일" 표기는 소비처가 계산한다.
   */
  validForHours: number;
};

/**
 * 상류(mgmt) 왕복이 **왜 그 결과가 됐는지**. 진단 전용 어휘다.
 *
 * `ok` 와 `campaign-off` 는 **둘 다 왕복 성공**이다. 그래서 이 값으로 배선을 검증하면
 * 캠페인 on/off 와 무관하게 판정할 수 있다 — 본문의 `available` 로는 불가능하다.
 *
 * ## ⚠️ auth 와 **헤더 이름은 같지만 매핑은 다르다** — 두 표면을 나란히 비교하지 말 것
 * auth(`auth.bhaptics.com` `src/app/api/welcome-coupon-offer/route.ts`)는 `fetch` 를 직접
 * 불러 상태코드를 보므로 더 잘게 가른다. 이쪽은 `ApiResponseCache` 를 거치는데 그 캐시가
 * **상태코드를 보지 않고**(`response.ok` 미확인) **엔벨로프가 온전하지 않으면 던지므로**,
 * 실패 사유가 `unreachable` 로 뭉쳐진다.
 *
 * ```
 * 상류 조건                     auth            여기
 * 비2xx(슬래시 401·호스트 404)  status-<code>   unreachable
 * 엔벨로프 결손·status:false    bad-shape       unreachable
 * available 이 boolean 아님     campaign-off    bad-shape
 * ```
 *
 * 🔴 이 차이를 없애려고 `ApiResponseCache` 를 고치지 말 것 — 그 캐시는 피처플래그 등
 * 다른 소비처가 현재 계약에 맞춰 동작 중이다. 차이를 **아는 채로** 읽는 것이 맞다.
 *
 * - `ok` — 발급 가능한 오퍼를 받았다
 * - `campaign-off` — 응답은 정상, 캠페인이 꺼져 있다(정상 상태)
 * - `bad-shape` — `message` 는 왔는데 `available` 이 boolean 이 아니다
 *   (상류 필드명 변경·엔벨로프 이중 벗기기)
 * - `bad-percent` — `available:true` 인데 할인율이 1~100 정수가 아니다
 * - `unreachable` — 왕복이 성립하지 않았다. 네트워크·호스트 오타뿐 아니라
 *   **`status:false` 나 비어 있는 `message`** 도 여기로 온다 — `ApiResponseCache` 가
 *   `if (status && data)` 를 통과 못 하면 **던지기** 때문이다(평문 응답·`message:null` 포함).
 *   ⚠️ 캐시는 `response.ok` 를 보지 않으므로 **HTTP 500 이어도 엔벨로프만 멀쩡하면 성공**으로
 *   친다 — 즉 `unreachable` 은 "비200" 과 같은 말이 아니다.
 */
export type WelcomeOfferUpstream =
  "ok" | "campaign-off" | "bad-shape" | "bad-percent" | "unreachable";

/** 노출용 값과 진단을 **분리해서** 나른다. `upstream` 은 본문에 싣지 않는다. */
export type WelcomeOfferResult = {
  offer: WelcomeCouponOffer;
  upstream: WelcomeOfferUpstream;
};

/** 진단 헤더 이름. auth 와 **이름만** 같다(매핑 차이는 위 표 참조). */
export const WELCOME_OFFER_UPSTREAM_HEADER = "x-welcome-offer-upstream";

/** 조회 실패·미제공 시의 값. **노출하지 않는 쪽**으로 떨어진다. */
export const WELCOME_OFFER_UNAVAILABLE: WelcomeCouponOffer = {
  available: false,
  percent: 0,
  validForHours: 0,
};

/**
 * 로그 접두사. 이 문자열로 검색해 발생 빈도를 센다
 * (`getFeatureFlags` 의 `[feature-flags] empty fallback` 과 같은 계열).
 */
export const WELCOME_OFFER_FALLBACK_EVENT =
  "[welcome-offer] unavailable fallback";

/**
 * ⚠️ **호스트는 `ApiHosts.admin`(mgmt)이다.** 이웃한 피처플래그 서비스가 `ApiHosts.sdk` 를
 * 쓰는데, 그건 sdk-apis 에도 같은 경로가 있어서다. 이 엔드포인트는 mgmt 에만 있으므로
 * `sdk` 로 부르면 조용히 실패한다(그리고 아래 폴백 때문에 화면은 "캠페인 없음" 으로 보인다).
 *
 * ⚠️ **경로 끝에 슬래시를 붙이지 말 것.** mgmt 의 공개 경로 화이트리스트가 정확 경로 등록이고
 * Spring MVC 6 은 trailing-slash 매칭이 기본 off 라, 슬래시가 붙으면 **401** 이 난다. 없는
 * 경로도 401 이라 증상이 구분되지 않는다(dev 실측 2026-09-10, mgmt `docs/welcome-coupon.md`).
 */
export const createWelcomeOfferService = () => {
  const endpoint = `${ApiHosts.admin}/api/v1/coupons/welcome-offer`;
  // 피처플래그와 같은 60초. 킬 스위치 지연이 늘지 않는다 — 이 오퍼의 `available` 은 서버에서
  // 이미 같은 플래그를 보므로, 캠페인을 끌 때의 반영 지연은 종전 플래그 경로와 동급이다.
  // ⚠️ **제네릭은 엔벨로프가 아니라 `message` 안쪽 모양이다.** `ApiResponseCache` 는
  // `const { message: data, status } = await response.json()` 로 **엔벨로프를 이미 벗겨**
  // `message` 를 돌려준다(`ApiResponse<Type>` 의 `message: Type`). 여기에 엔벨로프 타입을
  // 주면 소비처가 `.message` 를 한 번 더 벗기게 되고, 그 값은 항상 `undefined` 라
  // **캠페인이 켜져 있어도 네 표면이 전부 안 뜬다** — 타입체크는 통과하고 로그도 안 남는다
  // (2026-09-14 dev 실측: mgmt `available:true` / 이 라우트 `available:false`).
  // 이웃한 `getFeatureFlags` 가 `createInstance<FeatureFlag[]>` 로 **안쪽 배열**을 주는 것이 정본.
  const cache = ApiResponseCache.createInstance<Partial<WelcomeCouponOffer>>(
    `welcome-coupon-offer`,
    endpoint,
    60 * 1000,
  );

  /**
   * 오퍼와 **상류 왕복 결과**를 함께 돌려준다.
   *
   * 🔴 `available:false` 하나로는 **"캠페인이 꺼짐" 과 "배선이 깨짐" 이 구별되지 않는다.**
   * fail-closed 설계라 둘 다 같은 응답으로 수렴하기 때문이다. 그래서 왜 그 값인지를
   * 별도 채널(진단 헤더)로 내보낸다 — auth 와 같은 이름의 헤더다(매핑은 다르다).
   *
   * ⚠️ **`upstream` 을 본문에 넣지 말 것.** 소비처가 노출 판정에 섞어 쓰는 순간
   * fail-closed 가 깨진다. 읽는 사람만 보는 채널로 둔다.
   */
  const getOfferWithUpstream = async (): Promise<WelcomeOfferResult> => {
    try {
      const offer = await cache.getResponse();

      // 엔벨로프는 온전한데 **안쪽 payload 가 계약과 다를 때**만 여기 온다
      // (상류 필드명 변경·엔벨로프 이중 벗기기). 평문 응답·`message:null` 은 캐시가
      // 던지므로 여기가 아니라 `unreachable` 이다.
      // ★`available` 을 boolean 으로 좁히는 것도 여기다 — truthy 체크면 문자열 `"true"` 가
      // 광고를 켠다. 아래 `campaign-off` 분기는 **진짜 boolean false** 만 받는다.
      if (typeof offer?.available !== "boolean") {
        console.error(WELCOME_OFFER_FALLBACK_EVENT, {
          endpoint,
          impact: "welcome coupon surfaces hidden (bad shape)",
          // ⚠️ 상류 본문을 **통째로 남기지 않는다.** `bad-shape` 는 정의상 스키마가 모르는
          // 모양으로 드리프트한 순간이라 무엇이 들어올지 예측할 수 없다 — denylist 는 샌다
          // (AGENTS.md 함정 #16 "주문·결제 객체 전체 로깅 금지", allowlist 로 작성).
          // 진단에 필요한 건 "무슨 모양이었나" 이므로 타입과 키 이름만으로 충분하다.
          receivedType: typeof offer,
          receivedKeys:
            offer && typeof offer === "object" ? Object.keys(offer) : undefined,
        });
        return { offer: WELCOME_OFFER_UNAVAILABLE, upstream: "bad-shape" };
      }

      // 여기는 `available === false` — **왕복은 성공**했고 캠페인만 꺼진 정상 상태다.
      if (offer.available !== true) {
        return { offer: WELCOME_OFFER_UNAVAILABLE, upstream: "campaign-off" };
      }

      // ★**`percent` 도 같은 게이트에서 검증한다.** `available` 만 좁히고 값은
      // `Number(x) || 0` 로 흘리면, 필드가 없거나 숫자가 아닐 때(스키마 드리프트·필드명
      // 변경·게이트웨이가 일부만 평문화) **"0% 할인" 이 네 표면 전부에 뜬다** — 노출 누락이
      // 아니라 신뢰를 깎는 오고지다. 판정 술어는 서버(`claimBlockReason`)와 같은 1~100 을
      // 쓴다. 음수·`Infinity`·`NaN` 도 여기서 함께 걸린다.
      const percent = Number(offer.percent);
      if (!Number.isInteger(percent) || percent < 1 || percent > 100) {
        console.error(WELCOME_OFFER_FALLBACK_EVENT, {
          endpoint,
          impact: "welcome coupon surfaces hidden (invalid percent)",
          percent: offer.percent,
        });
        return { offer: WELCOME_OFFER_UNAVAILABLE, upstream: "bad-percent" };
      }

      // `validForHours` 는 **오퍼를 무효로 만들지 않는다.** 고지에 항상 쓰이는 값이 아니라
      // 1일 이상일 때만 표기되고(`toValidDays`), 손상되면 `0` → 표기 생략으로 degrade 한다.
      // 즉 "claim 에 쓰는 값은 fail-closed, 부가 표시는 degrade" 로 축을 나눈다 —
      // 여기서도 fail-closed 로 만들면 부가 필드 하나 때문에 유효한 캠페인이 안 보인다.
      const hours = Number(offer.validForHours);
      return {
        offer: {
          available: true,
          percent,
          validForHours: Number.isFinite(hours) && hours > 0 ? hours : 0,
        },
        upstream: "ok",
      };
    } catch (e) {
      // 동작은 **노출 안 함**이다. 잘못 광고하는 쪽이 노출 누락보다 나쁘다.
      // 엔드포인트와 소비 영향을 함께 남긴다 — 이 로그를 처음 보는 사람이 "쿠폰 하나 못
      // 읽은 것" 으로 축소해 읽지 않게 한다.
      console.error(WELCOME_OFFER_FALLBACK_EVENT, {
        endpoint,
        impact: "welcome coupon surfaces hidden (popup/badge/cart/PDP)",
        error: e,
      });
      return { offer: WELCOME_OFFER_UNAVAILABLE, upstream: "unreachable" };
    }
  };

  return {
    getOfferWithUpstream,

    /** 노출 판정만 필요한 소비처용. 진단은 버린다. */
    getOffer: async (): Promise<WelcomeCouponOffer> =>
      (await getOfferWithUpstream()).offer,

    invalidateCache: () => cache.invalidate(),
  };
};

// mgmt 는 모든 응답을 `ApiResponseType` 엔벨로프(`{ status, message, ... }`)로 감싸지만,
// 그 껍데기를 벗기는 것은 `ApiResponseCache` 의 책임이다. 여기서 엔벨로프 타입을 따로 두면
// 소비처가 한 번 더 벗기게 되므로 두지 않는다.
